import { storyblokCache } from "./storyblok-cache";
import { storyblokApi } from "@/lib/storyblok-api";

import { topicLabel } from "./faq-topics";
import { faqInRegion, type FaqStory } from "./faqs";
import type { Region } from "./region";

export type SearchEntry = {
  slug: string;
  name: string;
  href: string;
  section?: string;
  body: string;
  region: Region;
};

export type PageHit = {
  kind: "page";
  id: string;
  title: string;
  section: string;
  href: string;
  external?: boolean;
};

export type ContentHit = {
  kind: "content";
  id: string;
  title: string;
  section?: string;
  href: string;
  excerpt?: string;
};

export type FaqHit = {
  kind: "faq";
  id: string;
  title: string;
  topic: string;
  slug: string;
  topicValue: string;
};

export type Hit = PageHit | ContentHit | FaqHit;

export const SEARCH_STOPWORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "if",
  "as",
  "i",
  "me",
  "my",
  "mine",
  "you",
  "your",
  "yours",
  "he",
  "him",
  "his",
  "she",
  "her",
  "hers",
  "we",
  "us",
  "our",
  "ours",
  "they",
  "them",
  "their",
  "theirs",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "to",
  "of",
  "in",
  "on",
  "at",
  "for",
  "with",
  "about",
  "from",
  "by",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "do",
  "does",
  "did",
  "have",
  "has",
  "had",
  "can",
  "could",
  "will",
  "would",
  "should",
  "may",
  "might",
  "need",
  "want",
  "know",
  "tell",
  "get",
  "got",
  "how",
  "what",
  "where",
  "when",
  "why",
  "who",
  "which",
]);

export function normalizeQuery(raw: string): string {
  return raw
    .normalize("NFKC")
    .replace(/\p{Extended_Pictographic}/gu, " ")
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[^\p{L}\p{N}\s'-]/gu, " ")
    .replace(/(^|\s)['-]+/g, "$1")
    .replace(/['-]+(\s|$)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenizeQuery(raw: string): string[] {
  return normalizeQuery(raw)
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""))
    .filter((t) => t.length >= 3 && !SEARCH_STOPWORDS.has(t) && !/^(.)\1+$/.test(t));
}

export function scoreHit(
  haystack: { strong: string; weak: string },
  raw: string,
  tokens: string[],
): number {
  const strong = haystack.strong;
  const weak = haystack.weak;
  if (!raw) return 0;
  if (strong.startsWith(raw)) return 50;
  if (strong.includes(raw)) return 35;
  if (weak.includes(raw)) return 18;
  if (tokens.length === 0) return 0;

  let score = 0;
  let matched = 0;
  let strongMatched = false;
  for (const t of tokens) {
    if (strong.includes(t)) {
      score += 6;
      matched++;
      strongMatched = true;
    } else if (weak.includes(t)) {
      score += 2;
      matched++;
    }
  }
  if (matched === 0) return 0;
  if (tokens.length > 1 && matched < 2 && !strongMatched) return 0;
  return score;
}

export function withTextFragment(
  href: string,
  raw: string,
  tokens: string[],
  body?: string,
): string {
  if (!raw) return href;
  const lowerBody = body ? body.toLowerCase() : "";
  const anchors: string[] = [];

  if (lowerBody && lowerBody.includes(raw)) {
    anchors.push(raw);
  } else {
    if (!lowerBody && tokens.length === 0) anchors.push(raw);
    for (const t of tokens) {
      if (!t) continue;
      if (!lowerBody || lowerBody.includes(t)) {
        if (!anchors.includes(t)) anchors.push(t);
      }
    }
  }

  if (anchors.length === 0) return href;
  return href + "#:~:" + anchors.map((a) => `text=${encodeURIComponent(a)}`).join("&");
}

export function makeExcerpt(body: string, raw: string, tokens: string[]): string | undefined {
  if (!body) return undefined;
  const lower = body.toLowerCase();
  const needles = raw ? [raw, ...tokens] : tokens;
  let pos = -1;
  for (const n of needles) {
    if (!n) continue;
    pos = lower.indexOf(n);
    if (pos !== -1) break;
  }
  let start: number;
  let end: number;
  if (pos === -1) {
    start = 0;
    end = Math.min(body.length, 140);
  } else {
    start = Math.max(0, pos - 50);
    end = Math.min(body.length, pos + 110);
  }

  let slice = body.slice(start, end);
  if (start > 0) slice = slice.replace(/^\S+\s+/, "");
  if (end < body.length) slice = slice.replace(/\s+\S+$/, "");
  slice = slice.trim();
  if (!slice) return undefined;

  return (start > 0 ? "…" : "") + slice + (end < body.length ? "…" : "");
}

type RichTextNode = {
  type: string;
  content?: RichTextNode[];
  text?: string;
};

function richTextToPlainText(node: RichTextNode | undefined): string {
  if (!node) return "";
  if (typeof node.text === "string") return node.text;
  if (Array.isArray(node.content)) return node.content.map(richTextToPlainText).join(" ");
  return "";
}

const MAX_RESULTS = 30;

export function runSearch({
  q,
  pages,
  index,
  faqs,
  region,
  limit = MAX_RESULTS,
}: {
  q: string;
  pages: PageHit[];
  index: SearchEntry[];
  faqs: FaqStory[];
  region: Region;
  limit?: number;
}): Hit[] {
  const raw = normalizeQuery(q).toLowerCase();
  if (!raw) return [];
  const tokens = tokenizeQuery(raw);

  if (tokens.length === 0) return [];
  const scored: Array<{ hit: Hit; score: number }> = [];
  const seenHrefs = new Set<string>();
  const byHref = new Map<string, SearchEntry>();
  for (const e of index) byHref.set(e.href, e);

  for (const p of pages) {
    if (seenHrefs.has(p.href)) continue;
    const body = byHref.get(p.href)?.body;
    const strong = p.title.toLowerCase();
    const weak = `${p.title} ${p.section} ${body || ""}`.toLowerCase();
    const score = scoreHit({ strong, weak }, raw, tokens);
    if (score > 0) {
      scored.push({
        hit: { ...p, href: withTextFragment(p.href, raw, tokens, body) },
        score,
      });
      seenHrefs.add(p.href);
    }
  }

  for (const entry of index) {
    if (entry.region !== region) continue;
    const strong = entry.name.toLowerCase();
    const weak = `${entry.name} ${entry.section || ""} ${entry.body}`.toLowerCase();
    const score = scoreHit({ strong, weak }, raw, tokens);
    if (score === 0) continue;
    if (seenHrefs.has(entry.href)) continue;
    seenHrefs.add(entry.href);
    scored.push({
      hit: {
        kind: "content",
        id: `content-${entry.slug}`,
        title: entry.name,
        section: entry.section,
        href: withTextFragment(entry.href, raw, tokens, entry.body),
        excerpt: makeExcerpt(entry.body, raw, tokens),
      },
      score: strong.includes(raw) ? score : Math.round(score * 0.8),
    });
  }

  for (const f of faqs) {
    if (!faqInRegion(f, region)) continue;
    const question = (f.name || "").toLowerCase();
    const topic = f.content?.topic || "";
    const topicText = `${topic} ${topicLabel(topic)}`.toLowerCase();
    const answer = richTextToPlainText(f.content?.answer).toLowerCase();
    const strong = question;
    const weak = `${question} ${topicText} ${answer}`;
    const score = scoreHit({ strong, weak }, raw, tokens);
    if (score > 0) {
      scored.push({
        hit: {
          kind: "faq",
          id: f.uuid,
          title: f.name,
          topic: topicLabel(topic),
          slug: f.slug,
          topicValue: topic,
        },
        score: score - 2,
      });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.hit);
}

const EXCLUDED_COMPONENTS = new Set([
  "price",
  "review",
  "faq",
  "faq_modules",
  "faq_settings",
  "blog_modules",
  "nav_settings",
  "graph_data",
  "big_stat_data",
  "success_stat_data",
]);

const SECTION_LABELS: Record<string, string> = {
  treatments: "Treatments",
  testing: "Testing",
  pricing: "Pricing",
  about: "About",
  faqs: "FAQs",
  clinics: "Clinics",
  patients: "Patients",
};

type StoryShape = {
  name: string;
  slug: string;
  full_slug?: string;
  content?: Record<string, unknown>;
};

function toHref(fullSlug: string): string {
  let path = fullSlug;
  if (path.startsWith("ie/")) path = path.slice(3);
  if (path === "" || path === "home") return "/";
  if (path === "uk" || path === "uk/home") return "/uk";
  return path.startsWith("/") ? path : `/${path}`;
}

function sectionFromSlug(fullSlug: string): string | undefined {
  const path = fullSlug.replace(/^ie\//, "").replace(/^uk\//, "");
  const parts = path.split("/").filter(Boolean);
  if (parts.length < 2) return undefined;
  const top = parts[0].toLowerCase();
  return SECTION_LABELS[top] || top.charAt(0).toUpperCase() + top.slice(1).replace(/-/g, " ");
}

const SKIP_KEYS = new Set([
  "type",
  "marks",
  "attrs",
  "component",
  "plugin",
  "_editable",
  "_uid",
  "fieldtype",
  "seo",
  "noindex",
  "filename",
  "focus",
  "copyright",
  "source",
  "is_external_url",
  "meta_data",
  "content_type",
  "url",
  "cached_url",
  "linktype",
  "target",
  "href",
  "bg",
  "bg_color",
  "hero_bg_color",
  "color",
]);

const URL_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FOCAL_RE = /^\d+x\d+(:\d+x\d+)?$/;
const HEX_RE = /^#?[0-9a-f]{3,8}$/i;
const NUMERIC_RE = /^[\d.,\s-]+$/;

function isJunk(value: string): boolean {
  const v = value.trim();
  if (v.length < 2) return true;
  if (URL_RE.test(v)) return true;
  if (UUID_RE.test(v)) return true;
  if (FOCAL_RE.test(v)) return true;
  if (HEX_RE.test(v)) return true;
  if (NUMERIC_RE.test(v)) return true;
  if (/^[a-z][a-z0-9_-]*$/.test(v) && (v.includes("_") || v.includes("-"))) {
    return true;
  }
  return false;
}

function walk(node: unknown, out: string[]): void {
  if (node === null || node === undefined) return;
  if (typeof node === "string") {
    if (!isJunk(node)) out.push(node.trim());
    return;
  }
  if (typeof node === "number" || typeof node === "boolean") return;
  if (Array.isArray(node)) {
    for (const item of node) walk(item, out);
    return;
  }
  if (typeof node === "object") {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (key.startsWith("_")) continue;
      if (SKIP_KEYS.has(key)) continue;
      walk(value, out);
    }
  }
}

const BODY_MAX = 1500;

function flattenContent(content: unknown): string {
  const parts: string[] = [];
  walk(content, parts);
  const joined = parts.join(" ").replace(/\s+/g, " ").trim();
  return joined.slice(0, BODY_MAX);
}

export async function fetchSearchIndex(isDraft = false): Promise<SearchEntry[]> {
  const api = await storyblokApi();
  const entries: SearchEntry[] = [];
  try {
    let page = 1;
    while (true) {
      const { data } = await api.get(
        "cdn/stories",
        {
          per_page: 100,
          page,
          version: isDraft ? "draft" : "published",
        },
        storyblokCache(isDraft, ["search-index"]),
      );
      const stories = (data?.stories || []) as StoryShape[];
      for (const story of stories) {
        const component = story.content?.component as string | undefined;
        if (!component || EXCLUDED_COMPONENTS.has(component)) continue;
        const fullSlug = story.full_slug || story.slug;
        if (!fullSlug) continue;
        let region: Region;
        if (fullSlug.startsWith("uk/")) region = "uk";
        else if (fullSlug.startsWith("ie/")) region = "ie";
        else continue;
        const normalized = fullSlug.replace(/^(ie|uk)\//, "");
        if (normalized === "" || normalized === "home") continue;
        if (normalized === "lp" || normalized.startsWith("lp/")) continue;
        entries.push({
          slug: fullSlug,
          name: story.name,
          href: toHref(fullSlug),
          section: sectionFromSlug(fullSlug),
          body: flattenContent(story.content),
          region,
        });
      }
      if (stories.length < 100) break;
      page++;
      if (page > 30) break;
    }
  } catch {
    return [];
  }
  return entries;
}
